<template>
  <v-text-field
    v-model.trim="value"
    ref="textFieldRef"
    :type="type || (isDefined(numberValue) ? 'number' : 'text')"
    variant="outlined"
    hide-details="auto"
    class="e-input"
    :class="{ error: errorStyle }"
    :placeholder="placeholder"
    :disabled="!!disabled"
    :readonly="!!readonly"
    :rules="rules"
    :label="label"
    :maxlength="maxlength"
    :name="name"
    :error="error"
    :clearable="!!clearable"
    :focused="focused"
    :autofocus="autofocus"
    :error-messages="errorMessages"
    @focus="onFocus"
    @blur="onBlur"
    @click:append-inner="onAppendInnerClick"
    @update:model-value="onValueUpdate"
    v-maska="maska"
  >
    <template v-if="slots['prepend-inner']" #prepend-inner>
      <slot name="prepend-inner" />
    </template>
    <template v-if="slots['append-inner']" #append-inner="{ blur }">
      <slot name="append-inner" :blur="blur" />
    </template>
    <template v-if="slots.append" #append>
      <slot name="append" />
    </template>
    <template v-if="slots.label" #label>
      <slot name="label" />
    </template>
    <template v-if="clearable" #clear>
      <e-icon name="closeSvg" class="text-secondary" @click.stop.prevent="clear" />
    </template>

    <slot />
  </v-text-field>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { vMaska } from 'maska/vue';
import { computed, ref, watch, type Directive } from 'vue';
import { VInput } from 'vuetify/components';
import { EIcon } from '@/core/components/ui';
import { isDefined } from '@/core/utils/common';
import { isString } from '@/core/utils/type-guards';
import { numberFromString } from '@/core/utils/number';

type MaskaOptions = typeof vMaska extends Directive<unknown, infer T2> ? T2 : any;

const props = defineProps<{
  modelValue?: string | number | null;
  numberValue?: number | null;
  debounced?: boolean;
  label?: string;
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  readonly?: boolean;
  clearable?: boolean;
  autofocus?: boolean;
  focused?: boolean;
  errorStyle?: boolean;
  error?: boolean;
  rules?: VInput['rules'];
  maska?: MaskaOptions;
  maxlength?: string | number;
  type?: string;
  errorMessages?: string | boolean | (string | boolean)[];
}>();

const slots = defineSlots<{
  'prepend-inner'?: (props: unknown) => void;
  'append-inner'?: (props: { blur: () => void }) => void;
  append?: (props: unknown) => void;
  default?: (props: unknown) => void;
  label?: (props: unknown) => void;
}>();

const emit = defineEmits<{
  (event: 'focus'): void;
  (event: 'blur'): void;
  (event: 'click:appendInner'): void;
  (event: 'click:clear'): void;
  (event: 'update:modelValue', value: string | null): void;
  (event: 'update:numberValue', value: number | null): void;
}>();

const textFieldRef = ref<{
  validate: () => void;
  focus: () => void;
}>();
const isDirty = ref(false);

const value = ref<string | number | null>();

const errorMessages = computed<string | string[]>(() => {
  if (Array.isArray(props.errorMessages)) {
    return props.errorMessages.filter((message) => isString(message)) as string[];
  } else if (isString(props.errorMessages)) {
    return props.errorMessages as string;
  }

  return [];
});

watch(
  () => props.rules,
  () => {
    if (textFieldRef.value && isDirty.value) {
      setTimeout(() => {
        textFieldRef.value?.validate();
      }, 0);
    }
  },
  {
    deep: true,
    immediate: true,
  },
);

if (props.modelValue !== undefined) {
  watch(
    () => props.modelValue,
    (newValue) => {
      value.value = newValue;
    },
    {
      immediate: true,
    },
  );
}

if (props.numberValue !== undefined) {
  watch(
    () => props.numberValue,
    (newValue) => {
      value.value = newValue;
    },
    {
      immediate: true,
    },
  );
}

const onValueUpdate = useDebounceFn(
  (newValue: string) => {
    if (props.modelValue !== undefined) {
      emit('update:modelValue', newValue);
    } else if (props.numberValue !== undefined) {
      emit('update:numberValue', newValue ? numberFromString(newValue) : null);
    }
  },
  props.debounced ? 700 : 0,
);

const clear = () => {
  value.value = null;
  emit('click:clear');
};

const onFocus = () => {
  emit('focus');
};

const onBlur = () => {
  isDirty.value = true;
  emit('blur');
};

const onAppendInnerClick = () => {
  emit('click:appendInner');
};

const focus = () => {
  textFieldRef.value?.focus();
};

defineExpose({
  focus,
  input: textFieldRef,
});
</script>

<style scoped lang="scss"></style>
